import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Map, Package, DollarSign, Percent, Wallet, BellRing, FileText, History, Smartphone } from "lucide-react";

const OPCIONES = [
  { title: "Usuarios y Permisos", description: "Gestión de usuarios, roles y accesos al sistema", icon: Users, route: "/configuracion/usuarios" },
  { title: "Catálogo Territorial", description: "Administración de zonas y territorios de venta", icon: Map, route: "/configuracion/territorial" },
  { title: "Productos", description: "Configuración del catálogo de productos", icon: Package, route: "/configuracion/productos" },
  { title: "Listas de Precios", description: "Gestión de listas de precios y variantes", icon: DollarSign, route: "/configuracion/listas-precios" },
  { title: "Descuentos", description: "Reglas y políticas de descuentos aplicables", icon: Percent, route: "/configuracion/descuentos" },
  { title: "Comisiones", description: "Porcentajes, bonos y montos fijos de comisión", icon: Wallet, route: "/configuracion/comisiones" },
  { title: "Fichas Técnicas", description: "Descarga e imprime las fichas técnicas de los modelos", icon: FileText, route: "/configuracion/fichas-tecnicas" },
  { title: "Bitácora de accesos", description: "Historial de inicios de sesión con IP y dispositivo", icon: History, route: "/configuracion/accesos" },
  { title: "Guía de instalación", description: "Hoja membretada: instalar la app en celular y PC", icon: Smartphone, route: "/configuracion/guia-instalacion" },
];

export default function Configuracion() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [dias, setDias] = useState("5");
  const [umbral, setUmbral] = useState("3000");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/parametros").then(({ data }) => {
      setDias(String(data?.dias_aviso_vencimiento ?? "5"));
      setUmbral(String(data?.umbral_stock_bajo ?? "3000"));
    }).catch(() => {});
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put("/parametros", { dias_aviso_vencimiento: Number(dias) || 0, umbral_stock_bajo: Number(umbral) || 0 });
      toast.success("Parámetros guardados");
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="configuracion-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configuración del sistema</h1>
        <p className="text-muted-foreground mt-2">Administración y parámetros generales de la plataforma.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {OPCIONES.map((o) => (
          <Card key={o.route} onClick={() => navigate(o.route)} className="hover:bg-muted/50 cursor-pointer transition-colors" data-testid={`config-card-${o.route.split("/").pop()}`}>
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <div className="p-2 bg-primary/10 rounded-lg"><o.icon className="h-6 w-6 text-primary" /></div>
              <CardTitle className="text-xl">{o.title}</CardTitle>
            </CardHeader>
            <CardContent><CardDescription>{o.description}</CardDescription></CardContent>
          </Card>
        ))}
      </div>

      {isAdmin && (
        <Card data-testid="config-parametros-cobranza">
          <CardHeader className="flex flex-row items-center gap-4 pb-2">
            <div className="p-2 bg-primary/10 rounded-lg"><BellRing className="h-6 w-6 text-primary" /></div>
            <div>
              <CardTitle className="text-xl">Parámetros del sistema</CardTitle>
              <CardDescription>Cobranza e inventario</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
              <div className="space-y-2">
                <Label htmlFor="dias-aviso">Avisar cuántos días antes de vencer</Label>
                <Input id="dias-aviso" type="number" min="0" max="60" value={dias} onChange={(e) => setDias(e.target.value)} data-testid="param-dias-aviso" />
                <p className="text-xs text-muted-foreground">Avisos de "Por vencer" en Pedidos e Inicio.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="umbral-stock">Umbral de inventario bajo (kg)</Label>
                <Input id="umbral-stock" type="number" min="0" value={umbral} onChange={(e) => setUmbral(e.target.value)} data-testid="param-umbral-stock" />
                <p className="text-xs text-muted-foreground">Alerta de "Inventario bajo" cuando los kg disponibles caen a este valor o menos.</p>
              </div>
            </div>
            <Button onClick={guardar} disabled={saving} className="mt-4" data-testid="param-guardar">{saving ? "Guardando..." : "Guardar"}</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
